import { Check, ChevronDown } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  StatusIndicator,
  cn,
} from '@column/ui';

import { Mode } from '../_util/RootEntityContext';
import { ROUTE } from '~/app/routes';
import { useNavigate } from '~/lib/navigation';

interface RootEntityModeSelectorProps {
  mode: Mode;
}

export const RootEntityModeSelector: React.FC<RootEntityModeSelectorProps> = ({ mode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const liveLabelRef = useRef<HTMLSpanElement>(null);
  const sandboxLabelRef = useRef<HTMLSpanElement>(null);
  const [labelWidths, setLabelWidths] = useState<{ live: number; sandbox: number } | null>(null);
  const isLive = mode === Mode.Live;

  useLayoutEffect(() => {
    let isMounted = true;
    const updateLabelWidths = () => {
      if (isMounted && liveLabelRef.current && sandboxLabelRef.current) {
        setLabelWidths({
          live: liveLabelRef.current.offsetWidth,
          sandbox: sandboxLabelRef.current.offsetWidth,
        });
      }
    };

    updateLabelWidths();
    void document.fonts?.ready.then(updateLabelWidths);
    return () => {
      isMounted = false;
    };
  }, []);

  const handleModeChange = (nextMode: Mode) => {
    setIsOpen(false);
    if (nextMode === mode) return;

    const isAccountsPage = location.pathname.endsWith('/accounts');
    const pathname =
      nextMode === Mode.Live
        ? isAccountsPage
          ? ROUTE.ENTITIES_ROOT_ENTITY_LIVE_ACCOUNTS
          : ROUTE.ENTITIES_ROOT_ENTITY_LIVE
        : isAccountsPage
          ? ROUTE.ENTITIES_ROOT_ENTITY_SANDBOX_ACCOUNTS
          : ROUTE.ENTITIES_ROOT_ENTITY_SANDBOX;
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('starting_after');
    searchParams.delete('page_index');
    const search = searchParams.toString();

    navigate({ pathname, search: search ? `?${search}` : '' });
  };

  const triggerClassName = cn(
    'flex h-7 cursor-pointer items-center gap-1 rounded-md border px-1.5 text-xs outline-none transition-[background-color,border-color,color] duration-200 ease-out',
    isLive
      ? 'border-gray-200 bg-transparent hover:border-gray-300 hover:bg-gray-50 data-[state=open]:bg-gray-50 dark:border-white/10 dark:hover:border-white/[0.15] dark:hover:bg-white/[0.06] dark:data-[state=open]:bg-white/[0.06]'
      : 'border-orange-700/10 bg-orange-200/50 text-orange-600/80 hover:bg-orange-200/70 data-[state=open]:bg-orange-200/70 [&_[data-slot=dropdown-chevron]]:text-orange-600/80 dark:border-orange-400/20 dark:bg-orange-500/15 dark:text-orange-300 dark:hover:bg-orange-500/25 dark:data-[state=open]:bg-orange-500/25 dark:[&_[data-slot=dropdown-chevron]]:text-orange-300'
  );
  const labelWidth = labelWidths ? (isLive ? labelWidths.live : labelWidths.sandbox) : undefined;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={triggerClassName}
          aria-label={isLive ? 'Root entity environment: Live' : 'Root entity environment: Sandbox'}
        >
          <StatusIndicator mode={isLive ? 'live' : 'sandbox'} size="sm" className="shrink-0" />
          <span
            style={{ width: labelWidth }}
            className="relative inline-block overflow-hidden font-medium transition-[width] duration-200 ease-out"
          >
            <span
              ref={liveLabelRef}
              aria-hidden
              className="pointer-events-none invisible absolute top-0 left-0 whitespace-nowrap"
            >
              Live
            </span>
            <span
              ref={sandboxLabelRef}
              aria-hidden
              className="pointer-events-none invisible absolute top-0 left-0 whitespace-nowrap"
            >
              Sandbox
            </span>
            <AnimatePresence initial={false} mode="wait">
              <motion.span
                key={mode}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.1, ease: 'easeOut' }}
                className="inline-block whitespace-nowrap"
              >
                {isLive ? 'Live' : 'Sandbox'}
              </motion.span>
            </AnimatePresence>
          </span>
          <ChevronDown
            data-slot="dropdown-chevron"
            className={cn('size-3.5 transition-transform duration-200 ease-out', isOpen && 'rotate-180')}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-32 rounded-lg" align="end" sideOffset={4}>
        <DropdownMenuItem
          onSelect={() => handleModeChange(Mode.Sandbox)}
          className="gap-2 p-2"
          aria-current={!isLive ? 'true' : undefined}
        >
          <StatusIndicator mode="sandbox" className="shrink-0" />
          <span className="flex-1 font-medium">Sandbox</span>
          <Check className={cn('ml-auto size-4 shrink-0', isLive && 'invisible')} />
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => handleModeChange(Mode.Live)}
          className="gap-2 p-2"
          aria-current={isLive ? 'true' : undefined}
        >
          <StatusIndicator mode="live" className="shrink-0" />
          <span className="flex-1 font-medium">Live</span>
          <Check className={cn('ml-auto size-4 shrink-0', !isLive && 'invisible')} />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
